<div class="row g-4" [formGroup]="form">
    <div class="col-12 col-lg-5 d-flex flex-column gap-4">
        <div class="card p-3">
            <h3>{{ "Details" | translate }}</h3>
            <div class="form-group">
                <label class="form-label required" for="name">{{ "Offer title" | translate }}</label>
                <input
                    type="text"
                    class="form-control"
                    id="name"
                    formControlName="name"
                    [placeholder]="'Title of your offer' | translate"
                    [class.is-invalid]="isInvalid('name')"
                    [attr.maxlength]="TITLE_MAX_LENGTH"
                />
                <div class="invalid-feedback d-block" *ngIf="form.get('name')?.touched && form.get('name')?.hasError('titleTooShort')">
                    {{ "The title must contain at least 5 characters." | translate }}
                </div>
                <span class="form-text text-muted d-block text-end" aria-live="polite">{{ titleLength }} / {{ TITLE_MAX_LENGTH }}</span>
            </div>
            <div class="form-group">
                <label class="form-label" for="description">{{ "Description" | translate }}</label>
                <textarea
                    class="form-control"
                    id="description"
                    rows="3"
                    formControlName="description"
                    [placeholder]="'Describe what you\'re selling' | translate"
                ></textarea>
            </div>
        </div>

        <div class="card p-3" *ngIf="showSalesMandateBlock">
            <h3>
                {{ "create_offer_sales_mandate_title" | translate }}
                <span class="text-danger" aria-hidden="true">*</span>
            </h3>
            <p>{{ "create_offer_sales_mandate_explanation" | translate }}</p>
            <div *ngIf="salesMandateMedia" class="d-flex flex-column gap-2">
                <div class="d-flex align-items-center gap-2 text-success">
                    <span class="bi bi-check-circle-fill" aria-hidden="true"></span>
                    <span>{{ "create_offer_sales_mandate_uploaded" | translate }}</span>
                    <span *ngIf="salesMandateMedia.path" class="text-muted small text-break">{{ salesMandateMedia.path }}</span>
                </div>
                <div class="d-flex flex-wrap gap-2">
                    <button
                        type="button"
                        class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
                        (click)="uploadSalesMandate()"
                        [disabled]="salesMandateUploading"
                    >
                        <span
                            *ngIf="salesMandateUploading"
                            class="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                        ></span>
                        {{ "create_offer_sales_mandate_replace" | translate }}
                    </button>
                    <button
                        type="button"
                        class="btn btn-outline-danger btn-sm"
                        (click)="removeSalesMandate()"
                        [disabled]="salesMandateUploading"
                    >
                        {{ "create_offer_sales_mandate_remove" | translate }}
                    </button>
                </div>
            </div>
            <ng-container *ngIf="!salesMandateMedia">
                <button
                    type="button"
                    class="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                    (click)="uploadSalesMandate()"
                    [disabled]="salesMandateUploading"
                >
                    <span *ngIf="salesMandateUploading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {{ "create_offer_sales_mandate_upload_pdf" | translate }}
                </button>
            </ng-container>
            <div class="text-danger small mt-2" *ngIf="salesMandateRequired && salesMandateTouched && !salesMandateMedia">
                {{ "create_offer_sales_mandate_required_error" | translate }}
            </div>
        </div>

        <!-- Traditional Automatic Variant Generation -->
        <div class="card p-3" *ngIf="hasVariantFilters() && !isUsingManualVariantSelection">
            <h3>{{ "variants" | translate }}</h3>
            <div class="d-flex justify-content-between align-items-start gap-2">
                <span [innerHTML]="'variants explanation' | translate"></span>
                <div
                    class="badge badge-lg"
                    [class.bg-success]="countVariants() > 0"
                    [class.bg-warning]="countVariants() > 0 && countVariants() < 100"
                    [class.bg-danger]="countVariants() === 0 || countVariants() > 100"
                >
                    {{ "variants count" | translate: { count: countVariants() } }}
                </div>
            </div>
            <ng-container *ngFor="let filterVariant of variantFilters" formGroupName="variants">
                <app-filter-variant
                    [form]="getVariantsGroupForm()"
                    [filter]="filterVariant"
                    [platform]="data.platformData.platform"
                    *ngIf="hasFilterControl(getVariantsGroupForm(), filterVariant.getCode())"
                ></app-filter-variant>
            </ng-container>
        </div>
        <div class="card p-3" *ngIf="sizeGuideList">
            <h3>{{ "Size guide" | translate }}</h3>
            <p [innerHTML]="'offer_explanation_size_guide' | translate"></p>
            <div class="form-group">
                <label class="form-label required">{{ "Size guide" | translate }}</label>
                <app-select
                    formControlName="sizeGuide"
                    [isInvalid]="isInvalid('sizeGuide')"
                    [multiple]="false"
                    [options]="sizeGuideList"
                    [required]="true"
                ></app-select>
            </div>
            <button type="button" class="btn btn-outline-primary mt-2" (click)="openNewSizeGuideModal()">
                {{ "new_size_guide_button" | translate }}
            </button>
        </div>
    </div>
    <div class="col-12 col-lg-7">
        <div class="card p-3">
            <h3>{{ "More details" | translate }}</h3>
            <p>{{ "Create offer more details explanation" | translate }}</p>
            <ng-container *ngIf="filtersLoading; else filterList">
                <div class="py-3 text-center">
                    <app-loader></app-loader>
                </div>
            </ng-container>
            <ng-template #filterList>
                <div class="row mt-2">
                    <ng-container *ngFor="let filter of displayFilters">
                        <ng-container *ngIf="hasFilterControl(getFilterGroupForm(), filter.getCode())">
                            <div class="col-12" *ngIf="compositionGroupBannerFor(filter) as banner">
                                <div class="small mb-2" [class.text-danger]="!banner.valid" [class.text-muted]="banner.valid">
                                    {{ compositionGroupTotalKey | translate: { label: banner.label, total: banner.total } }}
                                    <span *ngIf="!banner.valid"> — {{ compositionGroupInvalidKey | translate }}</span>
                                </div>
                            </div>
                            <div [ngClass]="filter.getColClass()">
                                <app-filter
                                    [filter]="filter"
                                    mode="forms"
                                    [form]="getFilterGroupForm()"
                                    [platform]="data.platformData.platform"
                                ></app-filter>
                            </div>
                        </ng-container>
                    </ng-container>
                </div>
            </ng-template>
        </div>
    </div>
    <!-- Manual Variant Selection - Full Width -->
    <div class="mb-4" *ngIf="hasVariantFilters() && isUsingManualVariantSelection" [formGroup]="form">
        <div class="card p-4">
            <app-offer-group-variant-selector
                [variantFilters]="variantFilters"
                [allFilters]="filters"
                [platform]="data.platformData.platform"
                [initialSelection]="data.filterData?.manualVariantSelection"
                [initialVariantGroups]="data.filterData?.variantGroups"
                (variantsChange)="onManualVariantChange($event)"
                (selectionChange)="onManualVariantSelection($event)"
            ></app-offer-group-variant-selector>
        </div>
    </div>
</div>
<div class="form-group d-flex justify-content-end pt-4">
    <button type="button" class="btn btn-lg btn-primary d-inline-flex align-items-center" (click)="submit()">
        {{ "Next" | translate }} <span class="bi ps-2 bi-arrow-right"></span>
    </button>
</div>
